import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Search, RotateCcw, Trash2, Loader2, Crown, Building2, Undo2, LogIn, Vote } from "lucide-react";
import { IANA_TIMEZONES } from "@/lib/timezones";

const PLANS = ["trial", "starter", "pro", "enterprise"];

export default function TenantsAdmin() {
  const { impersonate } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTenant, setDeleteTenant] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/tenants", { params: { include_deleted: includeDeleted } });
      setTenants(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load tenants"); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [includeDeleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = tenants.filter((t) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (t.name || "").toLowerCase().includes(s) || (t.slug || "").toLowerCase().includes(s);
  });

  const restore = async (t) => {
    try {
      await api.post(`/tenants/${t.id}/restore`);
      toast.success(`Restored ${t.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const doImpersonate = async (t) => {
    if (!window.confirm(`Sign in as ${t.name}? Your super-admin session will be restored when you click "Return to super admin".`)) return;
    try {
      await impersonate(t.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impersonation failed"); }
  };

  const patchPlan = async (t, plan) => {
    const prev = t.plan;
    setTenants((p) => p.map((x) => (x.id === t.id ? { ...x, plan } : x)));
    try {
      await api.patch(`/tenants/${t.id}`, { plan });
      toast.success(`${t.name} → ${plan}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan update failed");
      setTenants((p) => p.map((x) => (x.id === t.id ? { ...x, plan: prev } : x)));
    }
  };

  const toggleMagicWall = async (t) => {
    const next = !((t.addons || {}).magic_wall);
    const prev = t.addons || {};
    setTenants((p) => p.map((x) => (x.id === t.id ? { ...x, addons: { ...prev, magic_wall: next } } : x)));
    try {
      const r = await api.patch(`/tenants/${t.id}`, { addons: { ...prev, magic_wall: next } });
      setTenants((p) => p.map((x) => (x.id === t.id ? { ...x, addons: r.data.addons } : x)));
      toast.success(`Magic Wall ${next ? "enabled" : "disabled"} for ${t.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add-on update failed");
      setTenants((p) => p.map((x) => (x.id === t.id ? { ...x, addons: prev } : x)));
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">§ Super Admin</div>
        <h1 className="font-display font-bold text-2xl mt-1">Tenants</h1>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or slug" className="rounded-sm pl-7 h-8 text-sm" data-testid="tenants-search" />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} data-testid="tenants-include-deleted" />
          Show deleted
        </label>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={load} disabled={busy} data-testid="tenants-refresh">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />} Refresh
        </Button>
        <div className="flex-1" />
        <Button className="rounded-sm h-8 text-xs tenant-btn" onClick={() => setCreateOpen(true)} data-testid="tenants-create">
          <Plus className="w-3.5 h-3.5 mr-1" /> New tenant
        </Button>
      </div>

      <div className="panel overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2">Name / slug</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-left px-3 py-2">Add-ons</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              const deleted = !!t.deleted_at;
              return (
                <tr key={t.id} className={`border-t border-border ${deleted ? "opacity-60" : ""}`} data-testid={`tenant-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-sm flex items-center justify-center text-white text-[10px] font-bold" style={{ background: t.branding?.primary_color || "#3B82F6" }}>
                        {(t.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-display font-bold flex items-center gap-1">
                          {t.name}
                          {deleted && <Badge className="text-[9px] uppercase font-mono bg-rose-500/15 text-rose-500 border border-rose-400/40">deleted</Badge>}
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground">{t.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{t.contact_email || "—"}</td>
                  <td className="px-3 py-2">
                    <select
                      className="text-xs rounded-sm border border-border bg-background px-1.5 py-0.5"
                      value={t.plan}
                      onChange={(e) => patchPlan(t, e.target.value)}
                      disabled={deleted}
                      data-testid={`tenant-plan-${t.id}`}
                    >
                      {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleMagicWall(t)}
                      disabled={deleted}
                      data-testid={`tenant-magicwall-${t.id}`}
                      title="Magic Wall interactive election map add-on"
                      className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[10px] font-mono uppercase tracking-wide transition ${
                        (t.addons || {}).magic_wall
                          ? "border-fuchsia-400/50 bg-fuchsia-500/15 text-fuchsia-300"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Vote className="w-3 h-3" /> Magic Wall {(t.addons || {}).magic_wall ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <Badge variant="outline" className="text-[9px] uppercase font-mono border-border">
                      {t.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {deleted ? (
                        <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs border-emerald-500/40 text-emerald-500" onClick={() => restore(t)} data-testid={`tenant-restore-${t.id}`}>
                          <Undo2 className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      ) : (
                        <>
                          <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs border-fuchsia-500/40 text-fuchsia-500" onClick={() => doImpersonate(t)} data-testid={`tenant-impersonate-${t.id}`} title="Sign in as this tenant's admin (1-hour session)">
                            <LogIn className="w-3 h-3 mr-1" /> Sign in as
                          </Button>
                          <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs border-rose-500/40 text-rose-500" onClick={() => setDeleteTenant(t)} data-testid={`tenant-delete-${t.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No tenants match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateTenantDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(t) => { setTenants((p) => [t, ...p]); toast.success(`Created ${t.name}`); }} />
      <DeleteTenantDialog tenant={deleteTenant} onClose={() => setDeleteTenant(null)} onDeleted={(t) => { setTenants((p) => p.map((x) => (x.id === t.id ? t : x))); toast.success(`${t.name} soft-deleted`); }} />
    </div>
  );
}

function CreateTenantDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ slug: "", name: "", plan: "pro", contact_email: "", timezone: "UTC" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ slug: "", name: "", plan: "pro", contact_email: "", timezone: "UTC" }); }, [open]);

  const submit = async () => {
    if (!form.slug || !form.name) { toast.error("Slug and name are required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/tenants", form);
      onCreated(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="tenant-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Building2 className="w-4 h-4 text-fuchsia-400" /> New tenant</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Provision a fresh whitelabel workspace. Slug is permanent; every other field is editable later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Slug (a-z, 0-9, -)</Label>
            <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} className="mt-1 rounded-sm font-mono" placeholder="acme-media" data-testid="create-tenant-slug" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Organization name</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm" placeholder="Acme Media" data-testid="create-tenant-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Plan</Label>
              <select value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))} className="mt-1 w-full rounded-sm border border-border bg-background px-2 py-1.5 text-sm" data-testid="create-tenant-plan">
                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Timezone</Label>
              <select value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} className="mt-1 w-full rounded-sm bg-background border border-border font-mono text-xs px-2 py-2" data-testid="create-tenant-tz">
                {IANA_TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Contact email</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className="mt-1 rounded-sm" placeholder="admin@acme-media.com" data-testid="create-tenant-email" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="rounded-sm h-8 text-xs tenant-btn" onClick={submit} disabled={busy} data-testid="create-tenant-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteTenantDialog({ tenant, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (tenant) setConfirm(""); }, [tenant]);

  if (!tenant) return null;
  const namesMatch = confirm.trim() === (tenant.name || "").trim();

  const submit = async () => {
    if (!namesMatch) return;
    setBusy(true);
    try {
      const r = await api.delete(`/tenants/${tenant.id}`, { data: { confirm_name: confirm } });
      onDeleted(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={!!tenant} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="tenant-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-rose-500"><Trash2 className="w-4 h-4" /> Soft-delete tenant</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            This will suspend <b className="text-foreground">{tenant.name}</b> and prevent all its users from logging in. Data is <b>not</b> erased — you can restore any time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-sm border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-[11px] font-mono text-rose-500">
            To confirm, type the tenant name exactly: <b>{tenant.name}</b>
          </div>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm font-mono" data-testid="tenant-delete-confirm" placeholder={tenant.name} />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            className={`rounded-sm h-8 text-xs ${namesMatch ? "bg-rose-500 hover:bg-rose-600 text-white" : "opacity-50 cursor-not-allowed"}`}
            onClick={submit}
            disabled={!namesMatch || busy}
            data-testid="tenant-delete-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />} Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
